import { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Pagination from "@mui/material/Pagination";
import ClearIcon from "@mui/icons-material/Clear";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import WorkIcon from "@mui/icons-material/Work";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import { getPurchaseHistory, getAvailablePlans } from "data/controllers/purchaseHistoryController";

const purchaseColumns = [
  { Header: "user", accessor: "user", width: "25%", align: "left" },
  { Header: "order ID", accessor: "orderId", align: "center" },
  { Header: "amount", accessor: "amount", align: "center" },
  { Header: "subscription plan", accessor: "subscriptionPlan", align: "center" },
  { Header: "Status", accessor: "status", align: "center" },
  { Header: "Created at", accessor: "purchasedAt", align: "center" },
  { Header: "action", accessor: "action", align: "center" },
];

function PurchaseHistory() {
  const [purchaseRows, setPurchaseRows] = useState([]);
  const [purchasePage, setPurchasePage] = useState(1);
  const [purchaseLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [localSearch, setLocalSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function for consistent date formatting
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }, []);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      setPurchasePage(1);
    }, 500),
    []
  );

  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    setLocalSearch(value);
    debouncedSearch(value);
  }, [debouncedSearch]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Enter" && purchasePage !== 1) {
      setPurchasePage(1);
    }
  }, [purchasePage]);

  const handleClearSearch = useCallback(() => {
    setLocalSearch("");
    setSearchTerm("");
    setPurchasePage(1);
  }, []);

  const handleViewPurchase = useCallback((purchase) => {
    setSelectedPurchase(purchase);
    setViewDialogOpen(true);
  }, []);

  const handleSortChange = useCallback((event) => {
    setSortBy(event.target.value);
    setPurchasePage(1);
  }, []);

  useEffect(() => {
    async function fetchPurchases() {
      setLoading(true);
      setError(null);

      try {
        const { purchases, pagination } = await getPurchaseHistory(
          purchasePage,
          purchaseLimit,
          searchTerm,
          sortBy
        );

        const formattedPurchases = purchases.map((purchase) => ({
          purchaseId: purchase._id,
          user: (
            <MDBox display="flex" alignItems="center" lineHeight={1}>
              <MDAvatar
                src={purchase.profilePicture}
                name={purchase.userName || "User"}
                size="sm"
              />
              <MDBox ml={2} lineHeight={1}>
                <MDTypography display="block" variant="button" fontWeight="medium">
                  {purchase.userName || "Unknown User"}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {purchase.userEmail || "No email"}
                </MDTypography>
              </MDBox>
            </MDBox>
          ),
          orderId: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {purchase.orderId || "N/A"}
            </MDTypography>
          ),
          amount: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              ₹{(purchase.amount || 0).toLocaleString()}
            </MDTypography>
          ),
          subscriptionPlan: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {purchase.planName || "Unknown Plan"}
            </MDTypography>
          ),
          status: (
            <MDTypography variant="caption" color={purchase.transactionId ? "success" : "warning"} fontWeight="medium">
              {purchase.transactionId ? purchase.transactionId.startsWith('rfnd') ? 'Refunded' : "Completed" : "Pending"}
            </MDTypography>
          ),
          purchasedAt: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {formatDate(purchase.purchasedAt)}
            </MDTypography>
          ),
          action: (
            <MDBox display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                color="info"
                onClick={() => handleViewPurchase(purchase)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </MDBox>
          ),
        }));

        setPurchaseRows(formattedPurchases);
        setTotalPages(Math.ceil((pagination?.total || 0) / purchaseLimit));
      } catch (error) {
        console.error("Failed to fetch purchases:", error);
        setError(error.message || "Failed to load purchase history");
        setPurchaseRows([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    }

    fetchPurchases();
    return () => {
      debouncedSearch.cancel();
    };
  }, [purchasePage, purchaseLimit, searchTerm, sortBy, formatDate, handleViewPurchase]);

  const handlePageChange = useCallback((event, value) => {
    setPurchasePage(value);
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Purchase History
                </MDTypography>
              </MDBox>
              {error && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                </MDBox>
              )}
              <MDBox px={2} pt={2}>
                {/* Search, Filter and Sort Controls - Single line on desktop, stacked on mobile */}
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={2}
                  mb={2}
                  sx={{
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "stretch", md: "center" },
                    "& > *": {
                      width: { xs: "100%", md: "auto" }
                    }
                  }}
                >
                  {/* Search Bar */}
                  <MDBox
                    display="flex"
                    alignItems="center"
                    sx={{
                      flex: { xs: "none", md: 1 },
                      width: { xs: "100%", md: "auto" },
                      minWidth: 0
                    }}
                  >
                  <input
                    type="text"
                    placeholder="Search by user name, order ID, or plan name"
                    value={localSearch}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                    style={{
                      flex: 1,
                      padding: "10px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      minWidth: 0,
                    }}
                  />
                  {localSearch && (
                    <IconButton onClick={handleClearSearch} size="small" disabled={loading}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  </MDBox>

                  <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      sx={{
                        whiteSpace: "nowrap",
                        display: { xs: "none", sm: "block" }
                      }}
                    >
                      Sort by:
                    </MDTypography>
                    <Select
                      value={sortBy}
                      onChange={handleSortChange}
                      disabled={loading}
                      sx={{
                        height: "36px",
                        fontSize: "14px",
                        flex: 1,
                        minWidth: "140px",
                        "& .MuiSelect-select": {
                          padding: "8px 24px 8px 12px",
                        },
                      }}
                    >
                      <MenuItem value="newest">Newest to Oldest</MenuItem>
                      <MenuItem value="oldest">Oldest to Newest</MenuItem>
                      <MenuItem value="alphabetical">Alphabetical</MenuItem>
                    </Select>
                  </MDBox>
                </MDBox>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <MDBox p={3}>
                    <Grid container spacing={2}>
                      {[...Array(5)].map((_, index) => (
                        <Grid item xs={12} key={index}>
                          <Skeleton variant="rectangular" height={60} />
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                ) : purchaseRows.length === 0 && !error ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="h6" color="text">
                      No purchase history found
                    </MDTypography>
                    <MDTypography variant="body2" color="text" mt={1}>
                      {searchTerm
                        ? "Try adjusting your search criteria"
                        : "No purchases have been made yet"}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns: purchaseColumns, rows: purchaseRows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </MDBox>
              {!loading && purchaseRows.length > 0 && (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={2}
                  mt={2}
                  px={3}
                >
                  <Pagination
                    count={totalPages}
                    page={purchasePage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton={window.innerWidth > 768}
                    showLastButton={window.innerWidth > 768}
                    siblingCount={window.innerWidth > 768 ? 1 : 0}
                    boundaryCount={window.innerWidth > 768 ? 1 : 1}
                    disabled={loading}
                    sx={{
                      ml: { xs: 0, sm: 2 },
                      "& .MuiPaginationItem-root": {
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        minWidth: { xs: "28px", sm: "32px" },
                        height: { xs: "28px", sm: "32px" },
                      }
                    }}
                  />
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* View Purchase Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: 3, position: "relative" }}>
          <MDBox display="flex" alignItems="center" gap={2}>
            <MDAvatar
              src={selectedPurchase?.profilePicture}
              name={selectedPurchase?.userName}
              size="lg"
            />
            <MDBox flex={1}>
              <MDTypography variant="h5" fontWeight="medium" color="dark">
                {selectedPurchase?.userName}
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
                Order ID: {selectedPurchase?.orderId}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Purchased on {formatDate(selectedPurchase?.purchasedAt)}
              </MDTypography>
            </MDBox>
          </MDBox>
          {/* Close Icon */}
          <IconButton
            onClick={() => setViewDialogOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5"
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 3, px: 3 }}>
          <Grid container spacing={3}>
            {/* Name */}
            <Grid item xs={12} md={6}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                <MDTypography variant="h6" fontWeight="medium">
                  Name
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2" color="text" sx={{ ml: 3 }}>
                {selectedPurchase?.userName || "Unknown User"}
              </MDTypography>
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <EmailIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                <MDTypography variant="h6" fontWeight="medium">
                  Email
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2" color="text" sx={{ ml: 3 }}>
                {selectedPurchase?.userEmail || "Not provided"}
              </MDTypography>
            </Grid>

            {/* Phone Number */}
            {selectedPurchase?.phoneNumber && (
              <Grid item xs={12} md={6}>
                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                  <PhoneIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                  <MDTypography variant="h6" fontWeight="medium">
                    Phone Number
                  </MDTypography>
                </MDBox>
                <MDTypography variant="body2" color="text" sx={{ ml: 3 }}>
                  {selectedPurchase.phoneNumber}
                </MDTypography>
              </Grid>
            )}

            {/* Address */}
            {selectedPurchase?.address && (
              <Grid item xs={12} md={6}>
                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                  <HomeIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                  <MDTypography variant="h6" fontWeight="medium">
                    Address
                  </MDTypography>
                </MDBox>
                <MDTypography variant="body2" color="text" sx={{ ml: 3 }}>
                  {selectedPurchase.address}
                </MDTypography>
              </Grid>
            )}

            {/* Role */}
            <Grid item xs={12} md={6}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <WorkIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                <MDTypography variant="h6" fontWeight="medium">
                  Role
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2" color="text" sx={{ ml: 3 }}>
                {selectedPurchase?.role === "candidate" ? "Candidate" : "Recruiter"}
              </MDTypography>
            </Grid>

            {/* Plan Name */}
            <Grid item xs={12} md={6}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <CardMembershipIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                <MDTypography variant="h6" fontWeight="medium">
                  Plan Name
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2" color="text" sx={{ ml: 3 }}>
                {selectedPurchase?.planName || "Unknown Plan"}
              </MDTypography>
            </Grid>

            {/* Purchase Amount */}
            <Grid item xs={12}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <MDTypography variant="h6" fontWeight="medium" color="success">
                  Purchase Amount: ₹{(selectedPurchase?.amount || 0).toLocaleString()}
                </MDTypography>
              </MDBox>
              {selectedPurchase?.paymentMethod && (
                <MDTypography variant="body2" color="text">
                  Payment Method: {selectedPurchase.paymentMethod}
                </MDTypography>
              )}
              {selectedPurchase?.transactionId && (
                <MDTypography variant="body2" color="text">
                  Transaction ID: {selectedPurchase.transactionId}
                </MDTypography>
              )}
              {selectedPurchase?.status && (
                <MDTypography variant="body2" color="text">
                  Status: {selectedPurchase.status.charAt(0).toUpperCase() + selectedPurchase.status.slice(1)}
                </MDTypography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setViewDialogOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: "#1976d2",
              color: "#ffffff",
              fontWeight: "medium",
              "&:hover": {
                backgroundColor: "#1565c0",
                color: "#ffffff"
              }
            }}
          >
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default PurchaseHistory;
